import { useEffect, useState } from 'react';
import type { FeatureFlagTypes } from '@/shared/types';
import {
  SIMON_SAYS_FLAGS_URL,
  SIMON_SAYS_POLL_INTERVAL_MS,
} from '@/lib/simon-says-url';

export {
  SIMON_SAYS_FLAGS_URL,
  SIMON_SAYS_POLL_INTERVAL_MS,
  TAI_FEATURE_FLAGS_HTTP_URL,
  simonSaysFlagsUrl,
} from '@/lib/simon-says-url';

/**
 * ``useFlags()`` key. LaunchDarkly ``simonSays`` starts the Simon Says
 * fetch. The returned map replaces LaunchDarkly only when that map's own
 * ``simonSays`` flag is true.
 */
export const SIMON_SAYS_FLAG = 'simonSays';

const FEATURE_FLAG_ENVIRONMENTS = ['dev', 'staging', 'prod', 'local'] as const;

export type TaiFeatureFlagEnvironment = (typeof FEATURE_FLAG_ENVIRONMENTS)[number];

export type TaiFeatureFlagsSocketArgs = {
  username: string;
  email: string;
  userId: string;
  tenantId: string;
  tenantName: string;
  cellId: string;
  partition: string;
  environment: string;
};

/** True only when this map includes ``simonSays`` set to boolean true. */
export function isSimonSaysEnabled(
  flags: Record<string, FeatureFlagTypes> | null | undefined,
): boolean {
  return flags?.[SIMON_SAYS_FLAG] === true;
}

/**
 * Simon Says replaces LaunchDarkly only when both ``simonSays`` flags are
 * boolean true. LaunchDarkly's flag starts the fetch; the service map's own
 * flag decides whether that fetch is the live set. Either flag turning off
 * selects LaunchDarkly again.
 *
 * Returns ``ldFlags`` by reference when Simon Says is not the source, so a
 * service-only update does not look like a flag change.
 */
export function selectFeatureFlagSource<T extends Record<string, FeatureFlagTypes> | undefined>(
  ldFlags: T,
  serviceFlags: Record<string, FeatureFlagTypes> | null,
): T | Record<string, FeatureFlagTypes> {
  if (!isSimonSaysEnabled(ldFlags) || !isSimonSaysEnabled(serviceFlags)) {
    return ldFlags;
  }
  return serviceFlags;
}

/**
 * Fold the active flag source into the app's flag record.
 *
 * ``replacePreviousFlags`` drops keys left over from the other source. A
 * source switch always replaces. While LaunchDarkly stays selected, pass
 * false so a stream that omits a key does not clear it.
 */
export function applyActiveFeatureFlags<T extends Record<string, unknown>>(
  previous: T,
  stable: Record<string, unknown>,
  source: Record<string, unknown> | undefined,
  overrides: Record<string, unknown>,
  replacePreviousFlags: boolean,
): T {
  if (replacePreviousFlags) {
    return { ...stable, ...source, ...overrides } as T;
  }
  return { ...previous, ...stable, ...source, ...overrides };
}

const REQUIRED_FEATURE_FLAG_ARG_KEYS = [
  'username',
  'email',
  'userId',
  'tenantId',
  'cellId',
  'partition',
  'environment',
] as const;

/**
 * True when the fetch may start. ``tenantName`` is sent when the token has
 * it, but customer-pool tokens omit ``custom:tenant_name``, so a missing
 * name must not keep Simon Says from loading.
 */
export function taiFeatureFlagsReady(
  enabled: boolean,
  args: Partial<TaiFeatureFlagsSocketArgs>,
): boolean {
  if (!enabled) return false;
  return REQUIRED_FEATURE_FLAG_ARG_KEYS.every((key) => (args[key]?.trim() ?? '') !== '');
}

function claimString(claims: Record<string, unknown> | null, ...keys: string[]): string {
  if (!claims) return '';
  for (const key of keys) {
    const value = claims[key];
    if (typeof value === 'string' && value.trim() !== '') return value.trim();
  }
  return '';
}

/** Payload of a Cognito access token. Not signature-checked; the bearer is sent as-is. */
export function accessTokenClaims(
  accessToken: string | null | undefined,
): Record<string, unknown> | null {
  const segment = accessToken?.trim().split('.')[1];
  if (!segment) return null;
  try {
    let b64 = segment.replace(/-/g, '+').replace(/_/g, '/');
    while (b64.length % 4) b64 += '=';
    const json = JSON.parse(atob(b64)) as unknown;
    if (!json || typeof json !== 'object' || Array.isArray(json)) return null;
    return json as Record<string, unknown>;
  } catch {
    return null;
  }
}

/**
 * Identity for ``POST /flags/evaluate``. ``custom:tenant_id`` and
 * ``custom:cell_id`` come from the access token when it carries them. The
 * auth-state claims object omits those fields.
 */
export function taiFeatureFlagsIdentity(
  args: Partial<TaiFeatureFlagsSocketArgs>,
  accessToken?: string | null,
): TaiFeatureFlagsSocketArgs {
  const claims = accessTokenClaims(accessToken);
  const given = (value: string | undefined) => value?.trim() ?? '';
  const tokenUsername = claimString(claims, 'username', 'cognito:username');
  const tokenEmail = claimString(claims, 'email');
  const username = tokenUsername || given(args.username) || tokenEmail || given(args.email);
  const email = tokenEmail || given(args.email) || (username.includes('@') ? username : '');
  return {
    username,
    email,
    userId: claimString(claims, 'custom:user_id') || given(args.userId),
    tenantId: claimString(claims, 'custom:tenant_id') || given(args.tenantId),
    tenantName: claimString(claims, 'custom:tenant_name') || given(args.tenantName),
    cellId: claimString(claims, 'custom:cell_id') || given(args.cellId),
    partition: claimString(claims, 'custom:partition') || given(args.partition),
    environment: taiFeatureFlagEnvironment(
      claimString(claims, 'custom:env') || given(args.environment),
      claimString(claims, 'custom:cell_id') || given(args.cellId),
    ),
  };
}

/** ``cell-dev-…`` / ``cell-staging-…`` / ``cell-prod-…`` when ``custom:env`` is absent. */
function environmentFromCellId(cellId: string | null | undefined): TaiFeatureFlagEnvironment | '' {
  const cell = cellId?.trim().toLowerCase() ?? '';
  if (cell.startsWith('cell-dev-')) return 'dev';
  if (cell.startsWith('cell-staging-')) return 'staging';
  if (cell.startsWith('cell-prod-')) return 'prod';
  return '';
}

/**
 * Simon Says accepts only ``dev``, ``staging``, ``prod``, and ``local``.
 * ``local`` evaluates against the dev configuration. A cell id supplies the
 * environment when the stage claim is missing.
 */
export function taiFeatureFlagEnvironment(
  stage: string | null | undefined,
  cellId?: string | null,
): TaiFeatureFlagEnvironment {
  const value = (stage ?? '').trim().toLowerCase();
  if ((FEATURE_FLAG_ENVIRONMENTS as readonly string[]).includes(value)) {
    return value as TaiFeatureFlagEnvironment;
  }
  return environmentFromCellId(cellId) || 'local';
}

/** Body for ``POST /simon-says/v1/flags/evaluate``. */
export function taiFeatureFlagsEvaluateBody(args: TaiFeatureFlagsSocketArgs): {
  user_context: {
    username: string;
    email: string;
    user_id: string;
    tenant_id: string;
    tenant_name: string;
    cell_id: string;
    partition: string;
    environment: TaiFeatureFlagEnvironment;
  };
} {
  return {
    user_context: {
      username: args.username,
      email: args.email,
      user_id: args.userId,
      tenant_id: args.tenantId,
      tenant_name: args.tenantName,
      cell_id: args.cellId,
      partition: args.partition,
      environment: taiFeatureFlagEnvironment(args.environment),
    },
  };
}

/** The only HTTP call. Identity stays in the JSON body; the Cognito access token is the bearer. */
export function taiFeatureFlagsEvaluateRequest(
  args: TaiFeatureFlagsSocketArgs,
  flagsUrl: string = SIMON_SAYS_FLAGS_URL,
  accessToken?: string,
): {
  url: string;
  method: 'POST';
  headers: { 'Content-Type': 'application/json'; Authorization?: string };
  body: string;
} {
  const token = accessToken?.trim() ?? '';
  return {
    url: flagsUrl,
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(taiFeatureFlagsEvaluateBody(taiFeatureFlagsIdentity(args, token))),
  };
}

function flagsRecord(value: unknown): Record<string, FeatureFlagTypes> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, FeatureFlagTypes>;
}

/** Read a flag map from a ``POST /flags/evaluate`` body. */
export function taiFeatureFlagsFromPayload(
  payload: unknown,
): Record<string, FeatureFlagTypes> | null {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return null;
  const record = payload as { taiFeatureFlags?: unknown; flags?: unknown };
  const direct = flagsRecord(record.taiFeatureFlags);
  if (direct) return direct;
  if (!Array.isArray(record.flags)) return null;
  const flags: Record<string, FeatureFlagTypes> = {};
  for (const item of record.flags) {
    if (!item || typeof item !== 'object') continue;
    const key = (item as { key?: unknown }).key;
    if (typeof key !== 'string' || key.trim() === '') continue;
    flags[key] = (item as { value?: FeatureFlagTypes }).value as FeatureFlagTypes;
  }
  return flags;
}

/** Pull ``taiFeatureFlags`` off a JSON flag payload. */
export function taiFeatureFlagsFromMessage(
  raw: string,
): Record<string, FeatureFlagTypes> | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  return taiFeatureFlagsFromPayload(parsed);
}

/** Why the latest Simon Says flags request produced no map. */
export type TaiFeatureFlagsFetchFailure =
  | { kind: 'status'; status: number }
  | { kind: 'unreachable' };

export function failureFromHttpStatus(status: number): TaiFeatureFlagsFetchFailure {
  return { kind: 'status', status };
}

/** Network and other thrown fetches. An abort is not a failure to show. */
export function failureFromFetchThrow(error: unknown): TaiFeatureFlagsFetchFailure | null {
  if (error instanceof DOMException && error.name === 'AbortError') return null;
  if (error instanceof Error && error.name === 'AbortError') return null;
  return { kind: 'unreachable' };
}

let lastFetchFailure: TaiFeatureFlagsFetchFailure | null = null;
const fetchFailureListeners = new Set<() => void>();

function sameFetchFailure(
  left: TaiFeatureFlagsFetchFailure | null,
  right: TaiFeatureFlagsFetchFailure | null,
): boolean {
  if (left === right) return true;
  if (!left || !right || left.kind !== right.kind) return false;
  return left.kind !== 'status' || right.kind !== 'status' || left.status === right.status;
}

function publishFetchFailure(failure: TaiFeatureFlagsFetchFailure | null) {
  if (sameFetchFailure(lastFetchFailure, failure)) return;
  lastFetchFailure = failure;
  fetchFailureListeners.forEach((listener) => listener());
}

/** Latest Simon Says fetch failure, or null after a successful response. */
export function useTaiFeatureFlagsFetchFailure(): TaiFeatureFlagsFetchFailure | null {
  const [failure, setFailure] = useState<TaiFeatureFlagsFetchFailure | null>(lastFetchFailure);
  useEffect(() => {
    setFailure(lastFetchFailure);
    const listener = () => setFailure(lastFetchFailure);
    fetchFailureListeners.add(listener);
    return () => {
      fetchFailureListeners.delete(listener);
    };
  }, []);
  return failure;
}

let lastEvaluateBody: string | null = null;
const evaluateBodyListeners = new Set<() => void>();

function publishEvaluateBody(body: string) {
  if (lastEvaluateBody === body) return;
  lastEvaluateBody = body;
  evaluateBodyListeners.forEach((listener) => listener());
}

/** JSON body of the latest ``POST /flags/evaluate`` from this page. */
export function useTaiFeatureFlagsRequestBody(): string | null {
  const [body, setBody] = useState<string | null>(lastEvaluateBody);
  useEffect(() => {
    setBody(lastEvaluateBody);
    const listener = () => setBody(lastEvaluateBody);
    evaluateBodyListeners.add(listener);
    return () => {
      evaluateBodyListeners.delete(listener);
    };
  }, []);
  return body;
}

function featureValuesEqual(left: unknown, right: unknown): boolean {
  if (Object.is(left, right)) return true;
  if (typeof left !== 'object' || typeof right !== 'object' || left === null || right === null) {
    return false;
  }
  if (Array.isArray(left) || Array.isArray(right)) {
    if (!Array.isArray(left) || !Array.isArray(right) || left.length !== right.length) return false;
    return left.every((item, index) => featureValuesEqual(item, right[index]));
  }
  const leftRecord = left as Record<string, unknown>;
  const rightRecord = right as Record<string, unknown>;
  const leftKeys = Object.keys(leftRecord);
  const rightKeys = Object.keys(rightRecord);
  if (leftKeys.length !== rightKeys.length) return false;
  return leftKeys.every((key) => (
    Object.prototype.hasOwnProperty.call(rightRecord, key)
    && featureValuesEqual(leftRecord[key], rightRecord[key])
  ));
}

/** True when two evaluated maps carry the same keys and values. */
export function featureFlagMapsEqual(
  left: Record<string, FeatureFlagTypes> | null,
  right: Record<string, FeatureFlagTypes> | null,
): boolean {
  if (left === right) return true;
  if (!left || !right) return false;
  const leftKeys = Object.keys(left);
  const rightKeys = Object.keys(right);
  if (leftKeys.length !== rightKeys.length) return false;
  return leftKeys.every((key) => (
    Object.prototype.hasOwnProperty.call(right, key)
    && featureValuesEqual(left[key], right[key])
  ));
}

/**
 * While ``enabled``, POST the flags URL immediately, then again every
 * ``SIMON_SAYS_POLL_INTERVAL_MS``. A map is returned only after the service
 * responds. A failed poll keeps the last successful map and is retried on
 * the same interval.
 */
export function useTaiFeatureFlags(
  enabled: boolean,
  args: Partial<TaiFeatureFlagsSocketArgs>,
  endpoints?: { flagsUrl?: string | null; accessToken?: string | null },
): Record<string, FeatureFlagTypes> | null {
  const [flags, setFlags] = useState<Record<string, FeatureFlagTypes> | null>(null);
  const username = args.username?.trim() ?? '';
  const email = args.email?.trim() ?? '';
  const userId = args.userId?.trim() ?? '';
  const tenantId = args.tenantId?.trim() ?? '';
  const tenantName = args.tenantName?.trim() ?? '';
  const cellId = args.cellId?.trim() ?? '';
  const partition = args.partition?.trim() ?? '';
  const environment = args.environment?.trim() ?? '';
  const flagsUrl = endpoints?.flagsUrl?.trim() || SIMON_SAYS_FLAGS_URL;
  const accessToken = endpoints?.accessToken?.trim() ?? '';
  const identity = taiFeatureFlagsIdentity(
    { username, email, userId, tenantId, tenantName, cellId, partition, environment },
    accessToken,
  );
  const ready = taiFeatureFlagsReady(enabled, identity);

  // Drop a cached map in this render when the fetch is off, so the next
  // LaunchDarkly `simonSays` turn-on cannot apply the previous service list
  // before the new POST returns.
  if (!ready && flags !== null) {
    setFlags(null);
  }

  useEffect(() => {
    if (!ready || !accessToken) {
      if (!ready) {
        setFlags(null);
        publishFetchFailure(null);
      }
      return;
    }
    const context: TaiFeatureFlagsSocketArgs = identity;
    let closed = false;
    let posting = false;
    const abort = new AbortController();

    const postFlags = async () => {
      if (closed || posting) return;
      posting = true;
      try {
        const request = taiFeatureFlagsEvaluateRequest(context, flagsUrl, accessToken);
        publishEvaluateBody(request.body);
        const response = await fetch(request.url, {
          method: request.method,
          headers: request.headers,
          body: request.body,
          signal: abort.signal,
        });
        if (closed) return;
        if (!response.ok) {
          publishFetchFailure(failureFromHttpStatus(response.status));
          return;
        }
        publishFetchFailure(null);
        const next = taiFeatureFlagsFromPayload(await response.json());
        if (!next) return;
        // Keep the previous object when the values match, so a poll does not
        // re-render the page and read LaunchDarkly flags.
        setFlags((prev) => (featureFlagMapsEqual(prev, next) ? prev : next));
      } catch (error) {
        const failure = failureFromFetchThrow(error);
        if (failure && !closed) publishFetchFailure(failure);
      } finally {
        posting = false;
      }
    };

    void postFlags();
    const timer = setInterval(() => {
      void postFlags();
    }, SIMON_SAYS_POLL_INTERVAL_MS);

    return () => {
      closed = true;
      abort.abort();
      clearInterval(timer);
    };
  }, [
    ready,
    identity.username,
    identity.email,
    identity.userId,
    identity.tenantId,
    identity.tenantName,
    identity.cellId,
    identity.partition,
    identity.environment,
    flagsUrl,
    accessToken,
  ]);

  return ready ? flags : null;
}
